import { logWarn } from '@ptek/client-logger';
import { useEffect, useState } from 'react';
import { getAuthConfig } from '../../../config/auth';
import { useWidgetAuth } from '../../../context/AuthContext';

interface HostAuthStatus {
  shouldShowNoAccess: boolean;
  shouldShowLoading: boolean;
}

/**
 * Hook to determine when to show "no access" page
 * This happens when:
 * 1. Host seamless auth is enabled
 * 2. allowFallbackToCognito is false
 * 3. Auth is required
 * 4. User is not authenticated
 * 5. Host auth callback fails
 */
export const useHostAuthStatus = (): HostAuthStatus => {
  const { isAuthenticated, requireAuth, isLoading } = useWidgetAuth();
  const [hostAuthFailed, setHostAuthFailed] = useState(false);
  const [hasCheckedHostAuth, setHasCheckedHostAuth] = useState(false);

  const authConfig = getAuthConfig();
  const isHostSeamlessMode = !!authConfig.hostSeamlessAuth;
  const allowFallbackToCognito =
    authConfig.hostSeamlessAuth?.allowFallbackToCognito ?? true;

  useEffect(() => {
    if (!isHostSeamlessMode || isAuthenticated || isLoading || !requireAuth) {
      setHostAuthFailed(false);
      setHasCheckedHostAuth(false);
      return;
    }

    if (hasCheckedHostAuth) {
      return;
    }

    let isMounted = true;

    const testHostAuth = async () => {
      try {
        if (!authConfig.hostSeamlessAuth?.getTokenCallback) {
          if (isMounted) {
            setHasCheckedHostAuth(true);
          }
          return;
        }

        const token = await authConfig.hostSeamlessAuth.getTokenCallback();

        if (isMounted) {
          // If we get here without a token, host auth failed
          setHostAuthFailed(!token);
          setHasCheckedHostAuth(true);
        }
      } catch (error) {
        if (isMounted) {
          logWarn('useHostAuthStatus', 'Host auth callback failed:', error);
          setHostAuthFailed(true);
          setHasCheckedHostAuth(true);
        }
      }
    };

    // Small delay to let the auth system settle
    const timer = setTimeout(testHostAuth, 50);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [
    isHostSeamlessMode,
    isAuthenticated,
    isLoading,
    requireAuth,
    authConfig.hostSeamlessAuth,
    hasCheckedHostAuth,
  ]);

  const shouldShowNoAccess =
    isHostSeamlessMode &&
    !allowFallbackToCognito &&
    requireAuth &&
    !isAuthenticated &&
    !isLoading &&
    hasCheckedHostAuth &&
    hostAuthFailed;

  const shouldShowLoading =
    isHostSeamlessMode &&
    !allowFallbackToCognito &&
    requireAuth &&
    !isAuthenticated &&
    !isLoading &&
    !hasCheckedHostAuth;

  return {
    shouldShowNoAccess,
    shouldShowLoading,
  };
};
